import { afterNextRender, ChangeDetectionStrategy, Component, computed, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, switchMap } from 'rxjs';

/** Suffix appended to every browser-tab title, e.g. "Edit License Details : License RegTrack". */
const TAB_TITLE_SUFFIX = 'License RegTrack';

import { findActiveSidebarChildId, SIDEBAR_NAV_ITEMS, SidebarComponentComponent, SidebarNavItem } from
  '../component/sidebar-component/sidebar-component.component';
import { DASHBOARD_SIDEBAR_ID, dashboardRoleSidebarId, isDashboardUrl, withDashboardSidebarChildren } from
  '../component/dashboard-page/dashboard-role.model';
import { DashboardFilterStore } from './shared/state/dashboard-filter.store';
import { FILTER_RESTORE_KEYS } from
  '../shared-component/filter-restore/filter-restore.model';
import { FilterRestoreRegistry } from
  '../shared-component/filter-restore/filter-restore.registry';
import { TopBarComponentComponent } from
  '../component/top-bar-component/top-bar-component.component';
import { BreadcrumbBarComponentComponent } from
  '../component/breadcrumb-bar-component/breadcrumb-bar-component.component';
import { AppTermScopeSelectorComponent } from
  '../shared-component/app-term-scope-selector/app-term-scope-selector.component';
import { BreadcrumbService } from
  './shared/services/breadcrumb.service';
import { DashboardRefreshService } from
  './shared/services/dashboard-refresh.service';
import { TermScopeService } from
  './shared/services/term-scope.service';
import { DashboardOriginBreadcrumbService } from './shared/services/dashboard-origin-breadcrumb.service';
import { NavigationHistoryService } from
  './shared/services/navigation-history.service';
import { BreadcrumbSegment } from './shared/models/breadcrumb.model';
import { LanguageService } from './shared/services/language.service';
import { CustomerLogoService } from './shared/services/customer-logo.service';
import { FOOTER_PRIVACY_POLICY_URL, FOOTER_REGTRACK_LOGO_URL, FOOTER_TERMS_OF_USE_URL } from './constants/branding.constants';
import { UserService } from './shared/services/user.service';
import { SearchBarStore } from '../component/search-bar/search-bar.store';
import { SearchFilterSpec } from './shared/models/search-filter-spec.model';
import { SearchRoutingService } from './shared/services/search-routing.service';
import { SearchVocabularyStore } from './shared/state/search-vocabulary.store';

import { LegacyPortalHandoffService } from './shared/services/legacy-portal-handoff.service';
import { DropdownOptionTooltipService } from './shared/services/dropdown-option-tooltip.service';
import { IdleTimeoutService } from './shared/services/idle-timeout.service';
import { TokenRefreshService } from './shared/services/token-refresh.service';
import { IdleTimeoutWarningComponent } from '../component/idle-timeout-warning/idle-timeout-warning.component';
import { environment } from '../environments/environment';

/** Router paths the sidebar's "Home" item can emit (`/home`, the placeholder
    route). A Home click bounces to the legacy app in prod and goes to
    `/dashboard` in dev.

    Only `onSidebarNavigate` consults this — never a Router guard, so a direct
    `/home` URL entry stays in the SPA. Only an explicit Home click may leave it. */
const SIDEBAR_HOME_PATHS: ReadonlySet<string> = new Set(['/home']);

/** Maps a top-bar quick-action id (emitted by `TopBarComponentComponent`'s
    `action` output — see its template) to the in-app route it navigates to.
    Only the wired icons appear here; an unmapped action (Language / Help) is
    ignored (no navigation), so those icons render with a tooltip but don't route
    anywhere yet. */
const TOP_BAR_ACTION_ROUTES: Readonly<Record<string, string>> = {
  message: '/message-center',
  megaphone: '/announcements',
  bell: '/notifications',
};
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponentComponent,
    TopBarComponentComponent,
    BreadcrumbBarComponentComponent,
    AppTermScopeSelectorComponent,
    IdleTimeoutWarningComponent,
    TranslateModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  // Component-provided, NOT `providedIn: 'root'`: the logo lifecycle is one shell's
  // state, so each AppComponent gets its own — see {@link CustomerLogoService}.
  providers: [CustomerLogoService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly router          = inject(Router);
  private readonly activatedRoute  = inject(ActivatedRoute);
  private readonly location        = inject(Location);
  private readonly breadcrumbService = inject(BreadcrumbService);
  /** Back-stack tracker so a breadcrumb crumb can go BACK to an ancestor already in
      history instead of pushing a duplicate (see {@link goToCrumb}). */
  private readonly navigationHistory = inject(NavigationHistoryService);
  /** Injected eagerly (never read here) so it is already recording where each navigation
      starts when the first dashboard drill-down fires — see the service's JSDoc. */
  private readonly dashboardOrigin = inject(DashboardOriginBreadcrumbService);
  private readonly title           = inject(Title);

  /** Source of the `customer_id` the sidebar logo is fetched for. Read on every
      navigation, because the shell is constructed before login populates it. */
  private readonly userService     = inject(UserService);

  /** Owns the customer-logo request lifecycle (cache, in-flight tracking, stale-response
      rejection, the skeleton bound). Component-provided, not `providedIn: 'root'` — see
      {@link CustomerLogoService}. The shell only decides WHEN it is safe to ask; see
      {@link refreshCustomerLogo} and the `afterNextRender` gate in the constructor. */
  private readonly customerLogo    = inject(CustomerLogoService);

  /** Hands a sidebar Home click off to the legacy portal in production (see
      {@link redirectHomeToLegacyIfProd}). */
  private readonly portalHandoff   = inject(LegacyPortalHandoffService);

  /** Gives every Syncfusion dropdown option a native `title` on hover so a label
      too long for the popup stays readable. Installed once from the shell because
      the popups are portalled to `<body>`, out of reach of any directive. */
  private readonly optionTooltips  = inject(DropdownOptionTooltipService);
  /** Ends the session after 15 min idle (VAPT #8); started once after first render. */
  private readonly idleTimeout    = inject(IdleTimeoutService);
  /** Renews the API token shortly before it expires; started once after first render. */
  private readonly tokenRefresh   = inject(TokenRefreshService);

  /** Gates the sessionStorage-backed logo cache to the browser — this is an SSR
      app and the server render has no sessionStorage. */
  private readonly platformId      = inject(PLATFORM_ID);

  /** Instantiated for its boot side-effect only: registering it here (the root
      component, created before any view renders) registers the en/de translation
      dictionaries and sets the default language, so `| translate` resolves from
      the very first render — server and client. */
  private readonly languageService = inject(LanguageService);

  /** Resolves a `breadcrumbs.*` key to display text — used for the tab-title fallback
      chain (Task 6), where the source isn't already-resolved `BreadcrumbSegment.label`
      text (the leaf route's raw `data.label` key, or the default key). */
  private readonly translate = inject(TranslateService);

  /** Shared channel for the global header search term (NOT a query param). */
  private readonly searchStore     = inject(SearchBarStore);

  /** Resolves an intent-search dropdown selection to a target page + filters and navigates —
      see {@link onSearchResultSelect}. */
  private readonly searchRouting   = inject(SearchRoutingService);

  /** Global word list for the header's intent search. Primed here — not by
      `IntentSearchComponent`, whose header instance never re-mounts across navigations —
      so a post-login identity change is ever re-checked; see {@link refreshSearchVocabulary}. */
  private readonly searchVocabulary = inject(SearchVocabularyStore);

  /** Live-published "which Dashboard role view is actually rendered" — read by
      {@link deriveActiveSidebarChildId} to tint the right sub-item on a bare
      '/dashboard' entry, which never changes the URL. */
  private readonly dashboardFilters = inject(DashboardFilterStore);

  /** Bridge to the mounted dashboard page for the breadcrumb bar's Refresh button —
      {@link onDashboardRefresh} forwards the click through it. */
  private readonly dashboardRefresh = inject(DashboardRefreshService);

  /** Bridge to whichever term-scope page (Workspace / Reports) is currently mounted —
      same pass-through role as {@link dashboardRefresh}, for the breadcrumb bar's
      term-scope selector mount (2026-08-19 ADR D1/D6). Public + readonly: the template
      reads `termScope.handle()` directly to gate the mount and bind the control. */
  readonly termScope = inject(TermScopeService);

  /** Root filter-restore flag store. The shell marks the Dashboard's return flag on
      an INTRA-dashboard sidebar navigation (the Dashboard sub-menu is the former
      role-tab band, so switching sub-items must preserve the dashboard's persisted
      per-view filters exactly as the in-page tab switch did) — see
      {@link onSidebarNavigate}. */
  private readonly filterRestoreRegistry = inject(FilterRestoreRegistry);

  /** False until the first client render (i.e. hydration) has completed — see the
      `afterNextRender` call in the constructor. Gates the per-navigation logo refresh so
      an early `NavigationEnd` cannot resolve the brand mid-hydration. This gate stays in
      the shell on purpose: the component decides WHEN it is safe to ask, the service
      decides HOW. */
  private canResolveLogo = false;

  /** Customer logo shown in the sidebar (a `data:` URI), or `null` to fall back to
      the RegTrack/License wordmark — read straight off {@link CustomerLogoService}, which
      owns the resolution. Kept as a shell-named alias because the template and this
      component's spec address it here. */
  readonly sidebarLogoUrl = this.customerLogo.logoUrl;

  /** True while the customer logo is still UNKNOWN and the sidebar renders a skeleton
      rather than guessing a brand. Alias of {@link CustomerLogoService.pending}, which
      documents the hydration contract behind it. */
  readonly sidebarLogoPending = this.customerLogo.pending;

  /** The signed-in user's primary role code (`UserService.getRole()`), fed to the
      sidebar so it can hide role-gated sections (e.g. Masters → MGMT/CADMN/IMPT),
      replicating the legacy master page's `user_Roles.Contains(...)` gating. Read
      synchronously from session state and refreshed on every navigation (like the
      logo) so the post-login redirect resolves it without a manual reload; empty
      pre-login / on the server, which simply hides the gated items until known. */
  readonly sidebarRole = signal<string>('');

  /** Sidebar menu the shell binds: the base items with the Dashboard entry expanded
      into a Documents-style drop-down whose children are the user's dashboard role
      views (the former in-page role tabs — `get-user-license-roles`). Refreshed on
      every navigation, like {@link sidebarRole}, so the post-login redirect resolves
      the roles without a manual reload. With no roles yet (pre-login / SSR / a
      confirmed role-less payload) the Dashboard entry stays the base list's plain
      leaf, navigating to `/dashboard` where the shell explains the no-roles case. */
  readonly sidebarItems = signal<SidebarNavItem[]>(
    withDashboardSidebarChildren(SIDEBAR_NAV_ITEMS, []),
  );

  /** The sidebar sub-item the current route falls under (URL-derived on every
      navigation via `findActiveSidebarChildId`, so deep links and back/forward
      tint the right child), or `null` off any sub-item. The sidebar renders it
      with the persistent hover tint; the parent row keeps the active colour. */
  readonly activeSidebarChildId = signal<string | null>(null);

  /** Breadcrumb trail, derived from the route tree by `BreadcrumbService`
      and consumed by the template via `[segments]="breadcrumbs()"`. */
  readonly breadcrumbs = this.breadcrumbService.breadcrumbs;

  /** Gates the breadcrumb bar's Refresh button (the relocated "Refresh Now",
      2026-08-06) to dashboard mounts: `DashboardFilterStore.activeRole()` is
      non-null exactly while `DashboardPageComponent` is mounted (all three
      dashboard views) and null on every other page. */
  readonly showDashboardRefresh = computed(() => this.dashboardFilters.activeRole() !== null);

  /** Page title + sidebar active item — derived from the currently-activated
      route's `data` block. Signals so the OnPush view re-renders when the
      NavigationEnd subscription writes them outside a template event.
      `pageLabel` is a `breadcrumbs.*` key (Task 6) — the template resolves it
      via `| translate` (`app.component.html`). */
  readonly pageLabel       = signal('breadcrumbs.dashboard');
  readonly activeSidebarId = signal('dashboard');

  /** Whether the app shell (sidebar + top bar + breadcrumb) wraps the routed
      page. Off for full-screen routes that opt out via `data: { chromeless: true }`
      — currently only the dev token-login page, which renders as a bare centered
      card with no shell. Defaults to `true`; the NavigationEnd subscription flips
      it per route (SSR sets it before serialize, so the server HTML already omits
      the chrome — no hydration flash). A signal so the OnPush view reacts to it. */
  readonly showChrome = signal(true);

  /** Product version shown in the footer. */
  readonly productVersion: string = environment.PRODUCT_VERSION;

  /** Current calendar year, shown in the footer's copyright line. */
  readonly footerYear: number = new Date().getFullYear();

  /** RegTrack wordmark shown in the footer's "Powered by" line when a customer logo is in use. */
  readonly footerRegtrackLogoUrl = FOOTER_REGTRACK_LOGO_URL;

  /** Public TeamLease RegTech privacy policy, linked from the footer. */
  readonly footerPrivacyPolicyUrl = FOOTER_PRIVACY_POLICY_URL;

  /** Public TeamLease RegTech terms of use, linked from the footer. */
  readonly footerTermsOfUseUrl = FOOTER_TERMS_OF_USE_URL;

  /** Footer variant: a resolved customer logo gets "Powered by RegTrack™"; the RegTrack
      fallback (`null`) or a still-pending logo gets the plain © line, so the wrong brand
      never flashes while the logo resolves. */
  get hasCustomerLogo(): boolean {
    return !this.sidebarLogoPending() && !!this.sidebarLogoUrl();
  }

  /** Mobile drawer state (open/closed slide-in below 1024px). */
  readonly sidebarOpen = signal(false);

  /** Desktop rail collapse state (≥1024px). When true the sidebar is an
      icons-only rail and the flex `.app__main` grows to fill the freed space. */
  readonly sidebarCollapsed = signal(false);

  constructor() {
    /* Browser-tab title: "<current page> : License RegTrack". The page name is the
       deepest breadcrumb (the current page, accumulated even for nested tab routes;
       already-resolved display text — see BreadcrumbService), falling back to the
       leaf route's `label` KEY (resolved here through TranslateService, since it
       skips BreadcrumbService entirely), then a sane default. Reactive on the
       breadcrumbs signal (refreshes on every navigation) AND on a language switch
       (Task 6) — without the latter, a language change would leave the tab title in
       the old language until the next navigation. */
    effect(() => {
      this.languageService.current(); // re-title on a language switch
      const crumbs = this.breadcrumbs();
      const leafLabel = this.leafLabel();
      // `TranslateService.instant()` throws on an empty key, so an absent leaf
      // label (no routes / no `data.label`) must skip the call rather than
      // resolve it — same as the pre-Task-6 raw-string fallback chain.
      const pageName =
        (crumbs.length ? crumbs[crumbs.length - 1].label : leafLabel && this.translate.instant(leafLabel))
        || this.translate.instant('breadcrumbs.dashboard');
      this.title.setTitle(`${pageName} : ${TAB_TITLE_SUFFIX}`);
    });

    /* Subscribe to every NavigationEnd, walk to the leaf route, and
       pull `label` + `sidebarKey` out of the route's data. */
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => this.leaf(this.activatedRoute)),
        /* switchMap (not mergeMap) so each new navigation cancels the
           previous inner subscription — prevents the leaf-route data
           subscription leak on rapid navigations. */
        switchMap(route => route.data),
        takeUntilDestroyed(),
      )
      .subscribe(data => {
        this.pageLabel.set((data['label']      as string) ?? 'breadcrumbs.dashboard');
        this.activeSidebarId.set((data['sidebarKey'] as string) ?? 'dashboard');
        this.showChrome.set(!(data['chromeless'] as boolean | undefined));
        // The shell (and its sidebar) is created once at boot, before login
        // populates the customer id. Re-reading on every completed navigation
        // means the post-login redirect to /dashboard resolves the logo without
        // a manual refresh — cheap and idempotent (cache-guarded), mirroring the
        // top-bar avatar refresh.
        //
        // Gated on the first render for the same reason the initial call is deferred: a
        // `NavigationEnd` that lands mid-hydration would resolve the brand before the
        // sidebar is hydrated and strand the server's skeleton nodes. The deferred call
        // below covers the initial resolve, so nothing is lost by skipping here.
        if (this.canResolveLogo) {
          this.refreshCustomerLogo();
        }
        // Same rationale for the sidebar role gate: the role lands in session state
        // at login, so re-read it on each navigation to hide/show gated sections
        // (e.g. Masters) once the post-login redirect completes.
        this.sidebarRole.set(this.userService.getRole());
        // And for the Dashboard drop-down: its children come from the dashboard
        // roles prefetched at login, so re-derive them once the redirect lands.
        this.refreshSidebarItems();
        // And for the search vocabulary: IntentSearchComponent's header instance is
        // constructed once and never re-mounts, so this is the only re-check that ever
        // picks up the real post-login identity.
        this.refreshSearchVocabulary();
        // Which sub-item the settled URL falls under (must run AFTER
        // refreshSidebarItems so the freshly derived Dashboard children are
        // matched, not the previous navigation's).
        this.activeSidebarChildId.set(this.deriveActiveSidebarChildId());
      });

    // Initial attempt (a returning session may already have a customer id in
    // sessionStorage before the first NavigationEnd fires) — but DEFERRED to after the
    // first render, never run inline here. Resolving the logo synchronously in the
    // constructor makes the client hydrate a different `@if` branch than the server
    // serialised, which strands the server's nodes in the DOM until Angular's
    // stability-gated cleanup removes them; see {@link sidebarLogoPending}.
    // `afterNextRender` is a no-op on the server, which is exactly right — the SSR pass
    // must keep the skeleton.
    afterNextRender(() => {
      this.canResolveLogo = true;
      this.refreshCustomerLogo();
      // Browser-only, and after the first paint: the delegated hover listener has
      // nothing to do until there is a real pointer over a rendered popup.
      this.optionTooltips.install();
      this.idleTimeout.init();
      this.tokenRefresh.init();
    });
    this.sidebarRole.set(this.userService.getRole());
    this.refreshSidebarItems();
    this.refreshSearchVocabulary();
    this.activeSidebarChildId.set(this.deriveActiveSidebarChildId());

    /* A bare '/dashboard' entry never changes the URL, so DashboardPageComponent
       publishes which role view it resolved to LIVE (DashboardFilterStore.activeRole)
       rather than through a URL the NavigationEnd subscription above could read. Its
       ngOnInit can settle before or after that subscription's callback runs — Angular
       gives no ordering guarantee between the two — so this effect re-derives
       independently whenever the published role changes, instead of relying on the
       NavigationEnd-driven `.set()` above to already see it. */
    effect(() => {
      this.dashboardFilters.activeRole();
      this.activeSidebarChildId.set(this.deriveActiveSidebarChildId());
    });
  }

  /**
   * Re-derives the sidebar menu from the dashboard roles in session state
   * (`UserService.getUserLicenseRoles`): the Dashboard entry becomes a drop-down
   * with one sub-item per role view the user holds. Empty roles (pre-login, SSR,
   * a session predating the prefetch, or a payload confirming no roles) leave the
   * Dashboard entry a plain leaf — no sub-items, since no view is authorized —
   * mirroring `DashboardPageComponent`'s no-roles handling.
   * @returns {void}
   */
  private refreshSidebarItems(): void {
    this.sidebarItems.set(
      withDashboardSidebarChildren(SIDEBAR_NAV_ITEMS, this.userService.getUserLicenseRoles()),
    );
  }

  /**
   * The sidebar sub-item the current URL falls under, for the persistent hover
   * tint (`activeChildId`). Normally the child whose route matches the URL
   * ({@link findActiveSidebarChildId}).
   *
   * Special case — the bare `/dashboard` landing (post-login, reload, the `**`
   * wildcard target) renders a role view IN PLACE, so the URL stays `/dashboard`
   * and matches no `/dashboard/<role>` child: the parent Dashboard row lights
   * (route `sidebarKey`) but no child would from the URL alone. Two fallbacks,
   * in order:
   * 1. A SINGLE dashboard role: the drop-down has exactly one child, which is
   *    unambiguously the view being shown, so tint it too — the parent and its
   *    lone child then both read as selected on load.
   * 2. A multi-role user: which view bare `/dashboard` resolved to is not
   *    derivable from the URL, so fall back to the LIVE role
   *    `DashboardPageComponent` publishes once it resolves one
   *    ({@link DashboardFilterStore.activeRole}) — verified against this user's
   *    own children so an unresolved (`null`) or not-yet-authorized role never
   *    lights a sub-item that isn't actually there.
   * @returns The active child's id, or `null` when none applies.
   */
  private deriveActiveSidebarChildId(): string | null {
    const items = this.sidebarItems();
    const url = this.router.url;
    const matched = findActiveSidebarChildId(items, url);
    if (matched) {
      return matched;
    }
    if (isDashboardUrl(url)) {
      const children = items.find(item => item.id === DASHBOARD_SIDEBAR_ID)?.children ?? [];
      if (children.length === 1) {
        return children[0].id;
      }
      const liveRole = this.dashboardFilters.activeRole();
      if (liveRole) {
        const liveId = dashboardRoleSidebarId(liveRole);
        if (children.some(child => child.id === liveId)) {
          return liveId;
        }
      }
    }
    return null;
  }

  /**
   * Asks {@link CustomerLogoService} to resolve the sidebar's customer logo, which owns
   * the whole lifecycle: the per-tenant sessionStorage cache, the `/common/customer-image`
   * fetch, in-flight tracking, rejection of a response that outlived its tenant, and the
   * skeleton's pending state plus its timeout bound.
   *
   * The shell keeps only the decision of WHEN to ask. Every call site is gated on the
   * first client render ({@link canResolveLogo} / `afterNextRender`) so the client
   * hydrates the same `@if` branch the server serialised — resolving earlier strands the
   * server's skeleton nodes in the DOM until Angular's stability-gated cleanup runs.
   * @returns {void}
   */
  refreshCustomerLogo(): void {
    this.customerLogo.refresh();
  }

  /**
   * Primes the global search vocabulary for the CURRENT identity. Mirrors
   * {@link refreshCustomerLogo}: the shell — and the header's `IntentSearchComponent` within
   * it — is constructed once, before `/auth-redirect` populates the real customer/user id,
   * and never re-mounts across navigations. Re-priming here on every completed navigation is
   * the only way a post-login identity is ever picked up.
   *
   * No-ops until BOTH a customer id and a user id are known — `SearchVocabularyStore` keys
   * its fetch-once cache off `"customer|user"` (see its `identity()`), and its `subscribe`
   * unconditionally overwrites `_entries`/`inFlightFor` on whatever response lands, without
   * checking it against the identity that is *current* by the time it arrives. An unguarded
   * pre-login call at identity `"0|0"` would race the real post-login fetch: if its
   * (empty/degraded) response lands after the real one, it silently clobbers the just-loaded
   * vocabulary back to empty — reintroducing this exact bug through a different call site.
   * `AuthRedirectComponent.setUserServiceData()` always sets both ids together, so gating on
   * both is equivalent to gating on "identity known" — never a source of extra delay.
   *
   * `SearchVocabularyStore.ensureLoaded()` is itself idempotent/cache-guarded
   * (`loadedFor`/`inFlightFor`) once an identity IS known, so calling it repeatedly from here
   * is cheap and safe.
   *
   * Browser-only: the store's HTTP calls have no reason to fire during the SSR pass.
   * @returns {void}
   */
  private refreshSearchVocabulary(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.userService.getCustomerId() || !this.userService.getUserId()) {
      return;
    }
    this.searchVocabulary.ensureLoaded();
  }

  /** Walk down ActivatedRoute children until we hit the leaf. */
  private leaf(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) route = route.firstChild;
    return route;
  }

  /** The leaf route's `label` KEY (browser-tab fallback when no breadcrumb applies) —
      a `breadcrumbs.*` dictionary key, resolved by the caller through `TranslateService`
      (see the title effect above), not already-translated text. */
  private leafLabel(): string {
    return (this.leaf(this.activatedRoute).snapshot.data['label'] as string) ?? '';
  }

  /** Sidebar emits a full router path (sub-items carry a nested route
      like `/documents/critical`; top-level items emit `/${id}`). A "Home" path
      (see `SIDEBAR_HOME_PATHS`) leaves the SPA for the legacy app in production
      (see `redirectHomeToLegacyIfProd`); in dev it goes to `/dashboard` via
      {@link goHome}. All other items route in-app.

      A navigation between two Dashboard URLs (the Dashboard sub-menu is the former
      in-page role-tab band) first marks the Dashboard's filter-restore return flag:
      a "tab switch" must preserve the dashboard's persisted per-view filters, exactly
      as the in-page tab switch did (it never destroyed the shell). The flag is marked
      at THIS click site — the framework's explicit-intent rule — and BEFORE the
      navigation, because the entering page consumes it during activation, i.e. before
      the navigation promise resolves; a navigation that then fails/cancels takes the
      mark back so a stale flag can never hijack a later, unrelated entry. */
  onSidebarNavigate(path: string): void {
    if (SIDEBAR_HOME_PATHS.has(path)) {
      // Prod leaves for the legacy app; dev has no Home page, so Home = the dashboard.
      if (!this.redirectHomeToLegacyIfProd(path)) this.goHome();
      return;
    }
    // Already there (e.g. the active Dashboard sub-item re-clicked): the router
    // would ignore the same-URL navigation anyway — skip it so no flag is marked.
    if (path === this.router.url) return;

    const isDashboardTabSwitch = isDashboardUrl(path) && isDashboardUrl(this.router.url);
    if (isDashboardTabSwitch) {
      this.filterRestoreRegistry.markReturn(FILTER_RESTORE_KEYS.dashboard);
    }
    void this.router.navigateByUrl(path).then(
      navigated => {
        if (isDashboardTabSwitch && !navigated) {
          this.filterRestoreRegistry.consumeReturn(FILTER_RESTORE_KEYS.dashboard);
        }
      },
      () => {
        if (isDashboardTabSwitch) {
          this.filterRestoreRegistry.consumeReturn(FILTER_RESTORE_KEYS.dashboard);
        }
      },
    );
  }

  /** Hamburger toggle. On desktop (≥1024px) it collapses/expands the rail; below
      that breakpoint the sidebar is a slide-in drawer, so it opens/closes instead.
      Click handlers run only in the browser, so `window` is always defined.

      The desktop flip reflows `.app__main` (the rail's width change is instant —
      no width transition) without firing a window `resize`, and Syncfusion
      charts/grids re-measure their container ONLY on `resize` — so every
      already-rendered chart kept its stale canvas width (dead space beside the
      plot after collapsing, a clipped plot after expanding) until a full page
      reload. Dispatch a synthetic `resize` once the flip has rendered: the
      macrotask runs after change detection has laid the new width out, and
      Syncfusion's own internal ~500ms resize debounce then measures the settled
      layout. The drawer branch overlays content without reflowing it, so it
      needs no dispatch. */
  onMenuToggle(): void {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      this.sidebarCollapsed.update(c => !c);
      setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    } else {
      this.sidebarOpen.update(o => !o);
    }
  }
  /** Mirrors the sidebar's own open/close (e.g. backdrop tap) back into state. */
  onSidebarOpenChange(o: boolean): void { this.sidebarOpen.set(o); }

  /**
   * Handles a search *submit* from the header (Enter / search-icon click): trims
   * the term, no-ops on an empty term, publishes it to {@link SearchBarStore}, and
   * navigates to the results page. The term travels through the store (a signal),
   * NOT a `?q=` query param or sessionStorage — the results page reads it
   * reactively, so re-searching while already on `/searchBar` still re-fires the
   * fetch even though the route (and component) don't change.
   * @param term The raw term from the header search box.
   */
  onHeaderSearch(term: string): void {
    const trimmed = (term ?? '').trim();
    if (!trimmed) {
      return;
    }
    this.searchStore.setTerm(trimmed);
    this.router.navigate(['/searchBar']);
  }

  /**
   * Handles the header's clear action (the input's clear button): resets the
   * shared search term to empty. The results page reacts to the store term, so
   * clearing it drops the stale grids — without this the header input goes blank
   * while `/searchBar` keeps showing the previous term's results.
   */
  onHeaderClearSearch(): void {
    this.searchStore.setTerm('');
  }

  /** Handles the header's intent-search result selection — navigates via SearchRoutingService,
      the same deep-link mechanism dashboard-card drill-downs already use.
      @param spec The chosen interpretation from the intent-search dropdown. */
  onSearchResultSelect(spec: SearchFilterSpec): void {
    this.searchRouting.navigate(spec);
  }

  /** The top bar emits an action id (`language` / `megaphone` / `help` / `message` /
      `bell`) from its quick-action icon row. Resolve it to a route via
      {@link TOP_BAR_ACTION_ROUTES} and navigate in-app; an unmapped id is a
      no-op rather than a navigation to a wrong or non-existent page. */
  onTopBarAction(action: string): void {
    const path = TOP_BAR_ACTION_ROUTES[action];
    if (path) this.router.navigateByUrl(path);
  }

  /** Breadcrumb back arrow — pops one entry off browser history.
      Falls back to /dashboard if there's no prior history (e.g. user
      landed directly on a deep route via URL). */
  goBack(): void {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/dashboard');
    }
  }

  /**
   * A breadcrumb CRUMB was clicked (plain left-click). Navigate to it consistently
   * with the back arrow: if the crumb's page is already BEHIND the current one in
   * history, go BACK to it (`Location.historyGo(-n)`) so the pages in between are
   * dropped from the back stack; otherwise (a deep link / first visit, where the
   * ancestor was never visited) navigate forward normally.
   *
   * This is the fix for the breadcrumb back-loop: a plain `routerLink` always PUSHED,
   * so returning "up" the trail left the just-exited child page one step behind the
   * new one, and the next Back walked into it. Going back when the target is already
   * in history keeps the flow linear — `Dashboard → Workspace → Update Details →
   * back → Workspace → back → Dashboard`.
   */
  goToCrumb(segment: BreadcrumbSegment): void {
    if (segment.link == null) {
      return;
    }
    const target = this.router.serializeUrl(
      this.router.createUrlTree([segment.link], { queryParams: segment.queryParams ?? undefined }),
    );
    const stepsBack = this.navigationHistory.stepsBackTo(target);
    if (stepsBack != null) {
      this.location.historyGo(-stepsBack);
    } else {
      this.router.navigateByUrl(target);
    }
  }
  /** Breadcrumb Home button — a dashboard redirect ONLY, in every environment.
      Unlike the sidebar's Home item (see {@link onSidebarNavigate}), it never
      bounces to the legacy app. A no-op while already on a Dashboard route —
      there is nowhere for "Home" to take you. */
  goHome(): void {
    if (isDashboardUrl(this.router.url)) return;
    this.router.navigateByUrl('/dashboard');
  }

  /** Breadcrumb Refresh button (rendered only while {@link showDashboardRefresh}
      is true) — forwards the click to the mounted dashboard page via
      {@link DashboardRefreshService}, which re-fetches with shimmer loaders. */
  onDashboardRefresh(): void {
    this.dashboardRefresh.requestRefresh();
  }

  /** In production a SIDEBAR "Home" click leaves the SPA for the legacy app's
      Product Mapping page via {@link LegacyPortalHandoffService} — the same
      `Auth.aspx?ticket=` handoff Change Password uses, with a `'Home'` intent
      (the breadcrumb Home button never does this — it is a dashboard redirect
      only, see {@link goHome}). Returns true when it delegated to the handoff
      service so the caller stops; false in dev, where the caller continues with
      in-app navigation. `path` is the in-app Home path the caller already
      resolved (`/home`) — the fallback the handoff service lands
      on if the portal base is unusable or the ticket cannot be minted. */
  private redirectHomeToLegacyIfProd(path: string): boolean {
    if (environment.production === true) {
      this.portalHandoff.home(path);
      return true;
    }
    return false;
  }
}
